import { Component, computed, EventEmitter, inject, input, Output } from '@angular/core';
import dayjs from 'dayjs';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { PermissionType, WorkflowStep } from '@shared/services/gql.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';

@Component({
  imports: [TooltipDirective],
  selector: 'aux-warning-vendor-estimate',
  template: `
    <div class="bg-aux-warn2 p-4">
      <p>
        Auxilius administrators will begin processing upon receipt, but for faster results you can
        now
        <span
          class="aux-link cursor-pointer text-aux-blue"
          [auxTooltip]="disabledTooltipMessage()"
          (click)="handleClick()"
          [class.opacity-80]="!!disabledTooltipMessage()"
        >
          add it yourself
        </span>
        and see it instantly for the current month. Copy and paste directly from your Excel tracker!
      </p>
    </div>
  `,
})
export class WarningVendorEstimateComponent {
  private authService = inject(AuthService);
  private mainQuery = inject(MainQuery);
  private workflowQuery = inject(WorkflowQuery);

  disabledTooltipMessage = computed(() => {
    if (!this.hasPermissions()) {
      return this.notPermittedMessage;
    }

    if (!this.vendor()) {
      return 'Select a Vendor Contract to in-line enter Vendor Estimates.';
    }

    if (this.isVendorEstimatesLocked() && this.isSelectedMonthOpen()) {
      return MessagesConstants.VENDOR_ESTIMATES_LOCKED;
    }

    if (this.isWorkflowLocked() && this.isSelectedMonthOpen()) {
      return MessagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE;
    }

    return '';
  });

  private userHasAdjustPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS],
    sysAdminsOnly: false,
  });
  private userHasEditVEPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_EDIT_VENDOR_ESTIMATE],
    sysAdminsOnly: false,
  });

  hasPermissions = computed(() => this.userHasAdjustPermission() && this.userHasEditVEPermission());

  month = input.required<string>();

  isSelectedMonthOpen = computed(() => {
    return (
      dayjs(this.mainQuery.currentOpenMonth()).format('YYYY-MM') ===
      dayjs(this.month()).format('YYYY-MM')
    );
  });

  vendor = input.required<string>();

  @Output() linkClick = new EventEmitter();

  private isVendorEstimatesLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_VENDOR_ESTIMATES
  );
  private isWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
  );
  private notPermittedMessage = 'You do not have permission to edit monthly work performed.';

  handleClick() {
    if (!this.disabledTooltipMessage()) {
      this.linkClick.emit();
    }
  }
}
